import { useState } from "react";
import { useParams, useOutletContext } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle, CheckCircle2, ClipboardCheck, Info, Lightbulb, Loader2,
  PencilLine, Settings2, Send, Undo2, X,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { BUSINESS_MODEL_LABEL, RECORD_TYPE_LABEL, uahFull, dmy } from "@/lib/client360Labels";
import type { Client360Envelope } from "@/api/clients";
import { fetchBusinessProfile } from "@/api/businessProfile";
import { ProfileEditWizard } from "@/components/clients/ProfileEditWizard";
import { ClassificationDrawer } from "@/components/clients/ClassificationDrawer";
import { CustomerPurchasesSummary } from "@/components/clients/CustomerPurchasesSummary";
import {
  ADDITIONAL_ACTIVITY_LABEL, ANIMAL_TYPE_LABEL, ASSORTMENT_LABEL, BUSINESS_SCALE_LABEL,
  COMPETITOR_BRAND_LABEL, COUNT_BAND_LABEL, DECISION_MODEL_LABEL, ORDER_FREQUENCY_LABEL,
  PERSON_TYPE_LABEL, POTENTIAL_LABEL, PURCHASING_MODEL_LABEL, SCENARIO_LABEL,
  STALE_REASON_LABEL, VERIFY_STATE_LABEL, VERIFY_STATE_TONE, answerValue, humanizeKey,
} from "@/lib/profileLabels";
import {
  fetchClient360Profile, profileTransition, resolveSuggestion,
  type Client360Profile, type ProfileTransition, type ProfileVerifyState,
} from "@/api/client360Profile";

/**
 * Client360 Этап 6 «Профіль» — type-aware SURFACE over 3.0A-4 BusinessProfile.
 *
 * VISUAL_Client360_Etap6_Profile_v1_1_LOCKED. Renders the §2 Classification +
 * Business-Profile blocks, §6 verify-lifecycle block (S/A-gated; Manager sees
 * «Очікує перевірки supervisor», NEVER self-verify), §4 breeder card (HIDE/SHOW),
 * §9 neutral state, and §8 meeting-report suggestions. Scenario/JSON editing is
 * DELEGATED to the Quick Form (clientRoutes.businessProfile) — never duplicated.
 */
export function ProfilePage() {
  const { holdingId } = useParams<{ holdingId: string }>();
  const id = holdingId ?? "";
  const qc = useQueryClient();
  const env = useOutletContext<Client360Envelope | undefined>();
  const [toast, setToast] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [classifyOpen, setClassifyOpen] = useState(false);

  const q = useQuery({
    queryKey: ["client360Profile", id],
    queryFn: ({ signal }) => fetchClient360Profile(id, signal),
    enabled: !!id,
    staleTime: 30_000,
  });

  // Completion Summary (§1) — backend-owned completeness + CONCRETE missing list
  // from the quick-form read model (shared query key with the wizard).
  const editableSurface = q.data?.visibility.surfaceMode === "commercial"
    || q.data?.visibility.surfaceMode === "breeder";
  const bpQ = useQuery({
    queryKey: ["businessProfile", id],
    queryFn: ({ signal }) => fetchBusinessProfile(id, signal),
    enabled: !!id && editableSurface,
    staleTime: 60_000,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["client360Profile", id] });
    qc.invalidateQueries({ queryKey: ["client360", id] });
    // FIX C — % completeness + «бракує» (businessProfile) і header-chips на
    // overview (client360Overview) теж backend-owned → рефетчимо, інакше stale.
    qc.invalidateQueries({ queryKey: ["businessProfile", id] });
    qc.invalidateQueries({ queryKey: ["client360Overview", id] });
  };

  const transitionM = useMutation({
    mutationFn: (vars: { t: ProfileTransition; reason?: string }) => {
      const p = q.data!;
      return profileTransition(id, vars.t, {
        profileId: p.profileId!,
        versionNumber: p.versionNumber ?? 0,
        reason: vars.reason,
      });
    },
    onSuccess: (_r, vars) => {
      setToast({ kind: "ok", text: TRANSITION_TOAST[vars.t] });
      invalidate();
    },
    onError: (e: unknown) => setToast({ kind: "err", text: errText(e) }),
  });

  const suggestionM = useMutation({
    mutationFn: (vars: { sid: string; action: "accept" | "reject" }) =>
      resolveSuggestion(vars.sid, vars.action),
    onSuccess: (_r, vars) => {
      setToast({ kind: "ok", text: vars.action === "accept" ? "Додано в чернетку профілю" : "Пропозицію відхилено" });
      invalidate();
    },
    onError: (e: unknown) => setToast({ kind: "err", text: errText(e) }),
  });

  if (q.isPending) {
    return (
      <div className="space-y-3 p-6 md:p-8">
        <div className="h-28 animate-pulse rounded-xl bg-muted" />
        <div className="h-40 animate-pulse rounded-xl bg-muted" />
      </div>
    );
  }
  if (q.isError || !q.data) {
    return (
      <div className="p-6 md:p-8">
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          Не вдалося завантажити профіль. <button onClick={() => q.refetch()} className="font-semibold underline">Повторити</button>
        </div>
      </div>
    );
  }

  const p = q.data;
  const v = p.visibility;
  const aa = p.allowedActions;
  const busy = transitionM.isPending || suggestionM.isPending;

  // §2 — EDIT is a wizard-drawer (НЕ page-form / НЕ navigate).
  const openEditor = () => setWizardOpen(true);

  // AC-2 — type-aware title: multi-LE → «· холдинг»; breeder → «· заводчик»;
  // single non-breeder → no suffix. NEVER «холдинг» for a single/breeder card.
  const isMultiLE = (env?.header.activeMembers ?? 0) > 1;
  const isBreederCard = p.recordType === "breeder_person"
    || v.surfaceMode === "breeder"
    || bpQ.data?.branch === "breeder";
  const titleSuffix = isMultiLE ? " · холдинг" : isBreederCard ? " · заводчик" : "";
  const bp = bpQ.data;
  const completenessPct = bp?.completeness?.percent ?? null;
  const missingLabels = bp?.missingFieldLabels ?? [];

  return (
    <div className="mx-auto max-w-[1080px] space-y-3.5 p-6 md:p-8">
      {/* §1 — type-aware header (VIEW = summary) */}
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">
            Бізнес-профіль<span className="font-normal text-muted-foreground">{titleSuffix}</span>
          </h1>
          {env?.header && (
            <div className="text-xs text-muted-foreground">{env.header.displayName || env.header.name}</div>
          )}
        </div>
        <StatePill state={p.verify.state} />
      </div>

      {/* §1 — Profile Completion Summary (% + CONCRETE missing + «Редагувати») */}
      {editableSurface && (
        <div className="rounded-xl border bg-card p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-xl font-bold tabular-nums">
              {completenessPct != null ? `${completenessPct}%` : "—"}{" "}
              <span className="text-sm font-normal text-muted-foreground">заповнено</span>
            </div>
            {aa.canOpenEditor && (
              <button
                onClick={openEditor}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
              >
                <PencilLine className="h-4 w-4" /> Редагувати профіль
              </button>
            )}
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${completenessPct ?? 0}%` }} />
          </div>
          {missingLabels.length > 0 ? (
            <div className="mt-2.5 rounded-lg border border-warning-foreground/20 bg-warning px-3 py-2 text-[12.5px] text-warning-foreground">
              Щоб подати на перевірку, бракує: <b>{missingLabels.join(" · ")}</b>
            </div>
          ) : completenessPct != null ? (
            <div className="mt-2.5 flex items-center gap-1.5 text-[12.5px] font-medium text-success-foreground">
              <CheckCircle2 className="h-4 w-4" /> Обов'язкові поля заповнені
            </div>
          ) : null}
        </div>
      )}

      {toast && (
        <div className={cn(
          "flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm",
          toast.kind === "ok"
            ? "border-success-foreground/20 bg-success text-success-foreground"
            : "border-destructive/30 bg-destructive-soft text-destructive-soft-foreground",
        )}>
          <span>{toast.text}</span>
          <button onClick={() => setToast(null)} className="shrink-0 opacity-60 hover:opacity-100"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* §9 — neutral technical/event/internal */}
      {v.surfaceMode === "neutral" && (
        <div className="flex items-start gap-3 rounded-xl border bg-muted/40 p-5 text-sm text-muted-foreground">
          <Settings2 className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <p className="font-medium text-foreground">Технічний / службовий запис</p>
            <p className="mt-1">{v.neutralMessage ?? "Для цього типу запису профіль не ведеться."}</p>
          </div>
        </div>
      )}

      {/* unclassified — classify first (no fill-CTA, no warnings) */}
      {v.surfaceMode === "unclassified" && (
        <div className="flex items-start gap-3 rounded-xl border border-warning-foreground/20 bg-warning p-5 text-sm text-warning-foreground">
          <Info className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <p className="font-medium">Тип клієнта не визначено</p>
            <p className="mt-1">Спершу класифікуйте картку (кнопка «Класифікувати» вгорі) — тоді відкриється відповідний профіль.</p>
          </div>
        </div>
      )}

      {/* §2 CLASSIFICATION — read-only card; edit → classification drawer (НЕ тут) */}
      {v.showClassification && p.classification && (
        <Block
          title="Класифікація"
          badge="read-only"
          action={
            <button onClick={() => setClassifyOpen(true)} className="text-xs font-semibold text-primary hover:underline">
              Змінити класифікацію →
            </button>
          }
        >
          <Grid>
            <Cell label="Тип CRM-запису" value={RECORD_TYPE_LABEL[p.classification.recordType] ?? p.classification.recordType} />
            <Cell label="Тип особи" value={PERSON_TYPE_LABEL[p.classification.personType] ?? p.classification.personType ?? "—"} />
            <Cell
              label="Бізнес-модель (основна)"
              value={p.classification.primaryBusinessModel
                ? (BUSINESS_MODEL_LABEL[p.classification.primaryBusinessModel] ?? p.classification.primaryBusinessModel)
                : "—"}
            />
            {v.showLegalForm && (
              <Cell label="Правова форма (з 1С/юрособи · read-only)" value={p.classification.legalForm ?? "—"} muted />
            )}
          </Grid>
          {p.classification.additionalActivities.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 border-t px-4 py-3">
              <span className="text-[11px] font-semibold text-muted-foreground">Додаткова діяльність:</span>
              {p.classification.additionalActivities.map((a) => (
                <Chip key={a} tone="violet">{ADDITIONAL_ACTIVITY_LABEL[a] ?? a}</Chip>
              ))}
            </div>
          )}
        </Block>
      )}

      {/* §2 BUSINESS PROFILE (commercial scenario) */}
      {v.showBusinessProfile && p.businessProfile && (
        <Block
          title={`Бізнес-профіль${p.businessProfile.scenario ? ` · сценарій «${SCENARIO_LABEL[p.businessProfile.scenario] ?? p.businessProfile.scenario}»` : ""}`}
          badge="як працює · з 3.0A-4"
          action={aa.canOpenEditor ? (
            <button onClick={openEditor} className="text-xs font-semibold text-primary hover:underline">Редагувати →</button>
          ) : undefined}
        >
          <Grid>
            {p.businessProfile.fields.businessScale && (
              <Cell label="Масштаб" value={BUSINESS_SCALE_LABEL[p.businessProfile.fields.businessScale] ?? p.businessProfile.fields.businessScale} />
            )}
            {p.businessProfile.fields.decisionModel && (
              <Cell label="Модель прийняття рішень" value={DECISION_MODEL_LABEL[p.businessProfile.fields.decisionModel] ?? p.businessProfile.fields.decisionModel} />
            )}
            {p.businessProfile.fields.purchasingModel && (
              <Cell label="Модель закупівель" value={PURCHASING_MODEL_LABEL[p.businessProfile.fields.purchasingModel] ?? p.businessProfile.fields.purchasingModel} />
            )}
            {p.businessProfile.fields.orderFrequency && (
              <Cell label="Частота замовлень" value={ORDER_FREQUENCY_LABEL[p.businessProfile.fields.orderFrequency] ?? p.businessProfile.fields.orderFrequency} />
            )}
            {p.businessProfile.fields.potentialEstimate && (
              <Cell label="Потенціал" value={POTENTIAL_LABEL[p.businessProfile.fields.potentialEstimate] ?? p.businessProfile.fields.potentialEstimate} />
            )}
            {p.businessProfile.fields.competitorBrands.length > 0 && (
              <Cell label="Конкурентні бренди" value={p.businessProfile.fields.competitorBrands.map((b) => COMPETITOR_BRAND_LABEL[b] ?? b).join(" · ")} />
            )}
            {p.businessProfile.fields.assortmentFocus.length > 0 && (
              <Cell label="Асортимент" value={p.businessProfile.fields.assortmentFocus.map((a) => ASSORTMENT_LABEL[a] ?? a).join(" · ")} />
            )}
            {p.businessProfile.scenarioAnswers.map((a) => (
              <Cell key={a.key} label={humanizeKey(a.key)} value={answerValue(a.value)} />
            ))}
          </Grid>
          {p.businessProfile.modules.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 border-t px-4 py-3">
              <span className="text-[11px] font-semibold text-muted-foreground">Модулі:</span>
              {p.businessProfile.modules.map((m) => (
                <Chip key={m} tone="green">{BUSINESS_MODEL_LABEL[m] ?? m}</Chip>
              ))}
            </div>
          )}
          {p.businessProfile.scenarioAnswers.length === 0
            && !p.businessProfile.fields.decisionModel
            && !p.businessProfile.fields.purchasingModel && (
            <div className="border-t px-4 py-3 text-sm text-muted-foreground">
              Сценарій ще не заповнено. {aa.canOpenEditor && (
                <button onClick={openEditor} className="font-semibold text-primary hover:underline">Заповнити</button>
              )}
            </div>
          )}
        </Block>
      )}

      {/* PB-2 «Купує у нас» — full (auto-BI кеш, read-only, D-049; всі типи) */}
      <CustomerPurchasesSummary holdingId={id} variant="full" />

      {/* §4 BREEDER card */}
      {v.showBreederCard && p.breeder && (
        <Block
          title="Профіль заводчика"
          action={aa.canOpenEditor ? (
            <button onClick={openEditor} className="text-xs font-semibold text-primary hover:underline">Редагувати →</button>
          ) : undefined}
        >
          <Grid>
            <Cell label="Тип тварин" value={p.breeder.animalTypes.map((a) => ANIMAL_TYPE_LABEL[a] ?? a).join(" · ") || "—"} />
            <Cell label="Поголів'я" value={p.breeder.countBand ? (COUNT_BAND_LABEL[p.breeder.countBand] ?? p.breeder.countBand) : "—"} />
            <Cell label="Породи" value={p.breeder.breeds.length ? p.breeder.breeds.join(", ") : "—"} />
            <Cell label="Розплідник" value={p.breeder.kennelName ?? "—"} />
            {/* v1.4 rename — узгоджено з wizard */}
            <Cell label="Клуб / система реєстрації" value={p.breeder.program ?? "—"} />
            <Cell label="Поточна схема годування" value={p.breeder.feedingProgram ?? "—"} />
            {p.breeder.purchaseHistory && (
              <Cell
                label="Закупівлі 365д"
                value={`${uahFull(p.breeder.purchaseHistory.amount365)} · з BI (read-only)`}
              />
            )}
          </Grid>
          <div className="border-t bg-muted/30 px-4 py-3 text-[12px] text-muted-foreground">
            🚫 НЕ показано / НЕ вимагається: юрособи · договір поставки · кредит-ліміт · ПДВ · ЄДРПОУ · правова форма · торгові точки · карта · B2B-ролі (ЛПР/закупівельник/бухгалтер).
          </div>
        </Block>
      )}

      {/* §8 — meeting-report enrichment suggestions */}
      {p.suggestions.length > 0 && (
        <div className="space-y-2">
          {p.suggestions.map((s) => (
            <div key={s.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-violet-foreground/20 bg-violet px-4 py-3">
              <Lightbulb className="h-5 w-5 shrink-0 text-violet-foreground" />
              <div className="min-w-0 flex-1 text-[13px] text-violet-foreground">
                <span className="font-semibold">{s.fieldLabel || s.fieldCode}</span>
                {s.message ? `: ${s.message}` : ""}
                {s.suggestedDisplay && <span> → <b>{s.suggestedDisplay}</b></span>}
              </div>
              {aa.canResolveSuggestion && (
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    disabled={busy}
                    onClick={() => suggestionM.mutate({ sid: s.id, action: "accept" })}
                    className="rounded-md bg-violet-foreground px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-violet-foreground/90 disabled:opacity-50"
                  >
                    Додати в чернетку
                  </button>
                  <button
                    disabled={busy}
                    onClick={() => suggestionM.mutate({ sid: s.id, action: "reject" })}
                    className="rounded-md border border-violet-foreground/30 px-2.5 py-1.5 text-xs font-medium text-violet-foreground hover:bg-violet disabled:opacity-50"
                  >
                    Відхилити
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* §6 — VERIFY block */}
      {v.showVerifyBlock && (
        <VerifyBlock
          profile={p}
          busy={busy}
          onSubmit={() => transitionM.mutate({ t: "submit" })}
          onWithdraw={() => transitionM.mutate({ t: "withdraw" })}
          onVerify={() => transitionM.mutate({ t: "verify" })}
          onReturn={() => {
            const reason = window.prompt("Причина повернення на доопрацювання (необов'язково):") ?? "";
            transitionM.mutate({ t: "return", reason });
          }}
          onMarkStale={() => transitionM.mutate({ t: "mark-stale", reason: "manual_supervisor" })}
          onEdit={openEditor}
        />
      )}

      {/* §2 — EDIT WIZARD (right-drawer, 4 steps) */}
      <ProfileEditWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        holdingId={id}
        displayName={env?.header.displayName || env?.header.name || ""}
        recordType={p.recordType}
        canSubmit={aa.canSubmit}
        verifyState={p.verify.state}
        canWithdraw={aa.canWithdraw}
        onWithdraw={() => transitionM.mutate({ t: "withdraw" })}
        onSubmitted={() => setToast({ kind: "ok", text: "Профіль подано на перевірку" })}
        onOpenClassification={() => { setWizardOpen(false); setClassifyOpen(true); }}
      />
      {env?.header && (
        <ClassificationDrawer
          open={classifyOpen}
          holdingId={id}
          header={env.header}
          businessModel={env.businessProfileSummary?.primaryBusinessModel ?? null}
          onClose={() => setClassifyOpen(false)}
        />
      )}
    </div>
  );
}

const TRANSITION_TOAST: Record<ProfileTransition, string> = {
  submit: "Профіль подано на перевірку",
  withdraw: "Подання відкликано",
  verify: "Профіль перевірено",
  return: "Повернено на доопрацювання",
  "mark-stale": "Профіль позначено застарілим",
};

function VerifyBlock({
  profile, busy, onSubmit, onWithdraw, onVerify, onReturn, onMarkStale, onEdit,
}: {
  profile: Client360Profile;
  busy: boolean;
  onSubmit: () => void;
  onWithdraw: () => void;
  onVerify: () => void;
  onReturn: () => void;
  onMarkStale: () => void;
  onEdit: () => void;
}) {
  const { verify, stale, allowedActions: aa } = profile;
  const state = verify.state;

  return (
    <div className="rounded-xl border bg-card p-4">
      <div className="flex flex-wrap items-center gap-3">
        <StatePill state={state} />
        <div className="text-[12.5px] text-muted-foreground">
          {state === "needs_review" && (
            <>
              {verify.submittedBy ? `Подав: ${verify.submittedBy}` : "Подано"}
              {verify.submittedAt ? ` · ${dmy(verify.submittedAt)}` : ""}
              {!aa.canVerify && <> · <b className="text-foreground">менеджер бачить:</b> «Очікує перевірки supervisor»</>}
            </>
          )}
          {state === "verified" && (
            <>Перевірив: {verify.reviewedBy ?? "—"}{verify.reviewedAt ? ` · ${dmy(verify.reviewedAt)}` : ""}</>
          )}
          {state === "stale" && stale.reason && (
            <span className="text-warning-foreground">Причина: {STALE_REASON_LABEL[stale.reason] ?? stale.reason}</span>
          )}
          {state === "draft" && "Чернетка — заповніть і подайте на перевірку"}
          {state === "empty" && "Профіль ще не заповнено"}
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {aa.canOpenEditor && (
            <button onClick={onEdit} disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-md border bg-background px-3 py-1.5 text-sm font-medium hover:bg-accent disabled:opacity-50">
              <PencilLine className="h-4 w-4" /> Редагувати
            </button>
          )}
          {aa.canSubmit && (
            <button onClick={onSubmit} disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Подати на перевірку
            </button>
          )}
          {aa.canWithdraw && (
            <button onClick={onWithdraw} disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-md border bg-background px-3 py-1.5 text-sm font-medium hover:bg-accent disabled:opacity-50">
              <Undo2 className="h-4 w-4" /> Відкликати
            </button>
          )}
          {aa.canVerify && (
            <button onClick={onVerify} disabled={busy} title="Тільки Supervisor/Admin"
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardCheck className="h-4 w-4" />} Перевірити
            </button>
          )}
          {aa.canReturn && (
            <button onClick={onReturn} disabled={busy} title="Тільки Supervisor/Admin"
              className="inline-flex items-center gap-1.5 rounded-md border bg-background px-3 py-1.5 text-sm font-medium hover:bg-accent disabled:opacity-50">
              <Undo2 className="h-4 w-4" /> Повернути
            </button>
          )}
          {aa.canMarkStale && (
            <button onClick={onMarkStale} disabled={busy} title="Тільки Supervisor/Admin"
              className="inline-flex items-center gap-1.5 rounded-md border border-warning-foreground/30 bg-background px-3 py-1.5 text-sm font-medium text-warning-foreground hover:bg-warning disabled:opacity-50">
              <AlertTriangle className="h-4 w-4" /> Позначити застарілим
            </button>
          )}
          {!aa.canVerify && (aa.canSubmit || aa.canWithdraw) && (
            <span className="text-[10.5px] text-muted-foreground">перевірка — S/A</span>
          )}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t pt-3 text-[11px] text-muted-foreground">
        {(["empty", "draft", "needs_review", "verified", "stale"] as ProfileVerifyState[]).map((s, i) => (
          <span key={s} className="flex items-center gap-1.5">
            {i > 0 && <span className="opacity-40">→</span>}
            <span className={cn(s === state && "font-bold text-foreground")}>{VERIFY_STATE_LABEL[s]}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function StatePill({ state }: { state: ProfileVerifyState }) {
  const tone = VERIFY_STATE_TONE[state] ?? "muted";
  const cls: Record<string, string> = {
    green: "bg-success text-success-foreground",
    amber: "bg-warning text-warning-foreground",
    blue: "bg-info text-info-foreground",
    muted: "bg-muted text-muted-foreground",
  };
  const Icon = state === "verified" ? CheckCircle2 : state === "stale" ? AlertTriangle : Info;
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold", cls[tone])}>
      <Icon className="h-3.5 w-3.5" /> {VERIFY_STATE_LABEL[state]}
    </span>
  );
}

// ── presentational primitives (match the locked visual) ──────────────────────

function Block({ title, badge, action, children }: { title: string; badge?: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-xl border bg-card">
      <div className="flex items-center gap-2 border-b bg-muted/30 px-4 py-2.5">
        <span className="text-[13.5px] font-bold">{title}</span>
        {badge && <span className="rounded px-1.5 py-0.5 text-[10.5px] font-semibold text-muted-foreground bg-muted">{badge}</span>}
        {action && <span className="ml-auto">{action}</span>}
      </div>
      {children}
    </div>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2">{children}</div>;
}

function Cell({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="border-b px-4 py-2.5 sm:[&:nth-child(odd)]:border-r">
      <div className="text-[11px] font-semibold text-muted-foreground">{label}</div>
      <div className={cn("mt-0.5 text-[13.5px]", muted && "text-muted-foreground")}>{value}</div>
    </div>
  );
}

function Chip({ children, tone }: { children: React.ReactNode; tone: "green" | "violet" }) {
  return (
    <span className={cn(
      "rounded-full px-2.5 py-0.5 text-[12px] font-semibold",
      tone === "green"
        ? "bg-success text-success-foreground"
        : "bg-violet text-violet-foreground",
    )}>
      {children}
    </span>
  );
}

function errText(e: unknown): string {
  if (e && typeof e === "object" && "message" in e) {
    return String((e as { message: unknown }).message) || "Помилка";
  }
  return "Помилка";
}
